import {
  Coding,
  QuestionnaireResponseItem,
  QuestionnaireResponseItemAnswer
} from '@medplum/fhirtypes'
import dayjs, { Dayjs } from 'dayjs'
import { useAssessmentContext } from 'contexts/AssessmentContext'
import { createValueCodingAnswer } from 'fhir/QuestionnaireResponse/utils'
import { QuestionnaireAnswerType } from 'components/Assessments/assessmentType'
import { isDateString, isValidStringValue } from '../helpers'
import { hasCodeAndDisplay } from '../../helpers'

export interface ValidateFormValuesType {
  type: QuestionnaireAnswerType
  linkId: string
  text: string
  value: string | number | Coding | Coding[] | Dayjs | null
}

export const useValidateAndUpdateValues = (): (({
  type,
  linkId,
  text,
  value
}: ValidateFormValuesType) => void) => {
  const { setResponseItems } = useAssessmentContext()

  const validateAndUpdateFormValues = ({
    type,
    linkId,
    text,
    value
  }: ValidateFormValuesType): void => {
    let formattedValue:
      | QuestionnaireResponseItemAnswer
      | QuestionnaireResponseItemAnswer[]
      | undefined
    switch (type) {
      case QuestionnaireAnswerType.DECIMAL: {
        if (typeof value === 'string') {
          const parsedValue = parseFloat(value)
          if (!isNaN(parsedValue)) {
            formattedValue = { valueDecimal: parsedValue }
          }
        } else if (typeof value === 'number') {
          formattedValue = { valueDecimal: value }
        }
        break
      }

      case QuestionnaireAnswerType.DATE: {
        const dateValue = value?.toString()
        if (dateValue && isDateString(dateValue)) {
          formattedValue = { valueDate: dayjs(dateValue).format('YYYY-MM-DD') }
        }
        break
      }

      case QuestionnaireAnswerType.STRING:
      case QuestionnaireAnswerType.TEXT: {
        if (typeof value === 'string') {
          const trimmedString = value.trim()
          if (isValidStringValue(trimmedString)) {
            formattedValue = { valueString: trimmedString }
          }
        }
        break
      }

      case QuestionnaireAnswerType.CHOICE: {
        if (value && Array.isArray(value)) {
          const answers = value
            .map((val) => {
              if (hasCodeAndDisplay(val)) {
                return createValueCodingAnswer(val)
              }
              return null
            })
            .filter((val): val is QuestionnaireResponseItemAnswer => val !== null)
          formattedValue = answers.length > 0 ? answers : undefined
        } else if (value && typeof value === 'object' && hasCodeAndDisplay(value)) {
          formattedValue = createValueCodingAnswer(value)
        }
        break
      }
    }

    let item: QuestionnaireResponseItem
    if (formattedValue) {
      item = {
        linkId,
        text,
        answer: Array.isArray(formattedValue) ? formattedValue : [formattedValue]
      }
    } else {
      item = {
        linkId,
        text
      }
    }

    setResponseItems([item])
  }

  return validateAndUpdateFormValues
}
